import { useEffect, useState } from 'react'
import type { PartnerAgency } from '../../types/agency'
import { listAgencies, createAgency } from './partnerAgenciesApi'

interface AgencySelectProps {
  value: number | null
  onChange: (agencyId: number | null) => void
  className?: string
  ariaLabel?: string
  /** Liste déjà chargée par l'appelant (évite un fetch redondant). Sans cette prop, le composant charge lui-même la liste au montage. */
  agencies?: PartnerAgency[]
}

const NONE = ''
const NEW = '__new__'

/** Sélecteur d'agence partenaire réutilisable (création projet, réglages). Charge les
 * agences de l'utilisateur au montage (sauf si fournies via la prop `agencies`), expose
 * "Non classé" et la création à la volée. */
export function AgencySelect({ value, onChange, className, ariaLabel, agencies: providedAgencies }: AgencySelectProps) {
  const [agencies, setAgencies] = useState<PartnerAgency[]>(providedAgencies ?? [])
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [creatingError, setCreatingError] = useState<string | null>(null)

  useEffect(() => {
    if (providedAgencies) { setAgencies(providedAgencies); return }
    let alive = true
    listAgencies()
      .then((data) => { if (alive) setAgencies(Array.isArray(data) ? data : []) })
      .catch(() => { if (alive) setAgencies([]) })
    return () => { alive = false }
  }, [providedAgencies])

  const handleSelectChange = (v: string) => {
    if (v === NEW) { setCreating(true); return }
    onChange(v === NONE ? null : Number(v))
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreatingError(null)
    try {
      const agency = await createAgency(newName.trim())
      setAgencies((prev) => [...prev, agency])
      onChange(agency.id)
      setCreating(false)
      setNewName('')
    } catch (err: any) {
      setCreatingError(err.message || 'Erreur')
    }
  }

  if (creating) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nom de l'agence"
          className={className}
        />
        <button type="button" onClick={handleCreate} className="text-sm text-orion-primary-text font-medium shrink-0">Créer</button>
        <button type="button" onClick={() => { setCreating(false); setNewName(''); setCreatingError(null) }} className="text-sm text-[#8B8FA7] shrink-0">Annuler</button>
        {creatingError && <p className="text-score-critical text-xs">{creatingError}</p>}
      </div>
    )
  }

  const selectValue = value == null ? NONE : String(value)

  return (
    <select aria-label={ariaLabel} value={selectValue} onChange={(e) => handleSelectChange(e.target.value)} className={className}>
      <option value={NONE}>Non classé</option>
      {agencies.map((a) => (
        <option key={a.id} value={a.id}>{a.name}</option>
      ))}
      <option value={NEW}>+ Nouvelle agence</option>
    </select>
  )
}
